import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Check, Copy, ExternalLink, Printer, ScanLine } from "lucide-react";
import { base_url } from "../../config";

type Link = {
  event: string | null;
  kind: "checkin" | "door" | "lookup";
  label: string;
  color: string;
  when: string | null;
  url: string;
  qr: string;
};

const CAPTION: Record<Link["kind"], string | null> = {
  checkin: "Hands over the badge, marks it collected and emails the certificate. Once per delegate.",
  door: null,
  lookup: "Reports which events a badge admits. Never opens a door.",
};

/**
 * Everything the door staff need in one place. Tabs rather than separate menu entries
 * because these all answer the same question — "how is the door running" — and they are
 * each too small to earn a page of their own.
 */
const TABS = [{ id: "links", name: "Link summary" }];

const AccessControl = () => {
  const [tab, setTab] = useState("links");

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center gap-2 no-print">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-blue-400" />
            Access control
          </h1>
        </header>

        <div className="flex items-center gap-1.5 flex-wrap no-print">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                tab === t.id
                  ? "bg-blue-500/25 text-blue-100 border-blue-400/50"
                  : "bg-slate-800/60 text-slate-300 border-slate-700/50 hover:border-slate-600/70"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {tab === "links" && <LinkSummary />}
      </div>
    </div>
  );
};

const LinkSummary = () => {
  const [links, setLinks] = useState<Link[]>([]);
  const [keyRequired, setKeyRequired] = useState(true);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${base_url}/api/admin/access_control_links`);
        setLinks(response.data.links || []);
        setKeyRequired(!!response.data.key_required);
      } catch (error) {
        console.error("Error fetching access control links:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      window.setTimeout(() => setCopied(null), 1500);
    } catch (error) {
      console.error("Clipboard unavailable:", error);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-400">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Printing a dark admin page wastes toner and comes out unreadable, so the sheet
          is forced to black on white and everything but the cards is dropped. */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .qr-sheet { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 12mm !important; }
          .qr-card { background: #fff !important; border: 1px solid #999 !important;
                     color: #000 !important; break-inside: avoid; page-break-inside: avoid; }
          .qr-card * { color: #000 !important; }
          .qr-card .qr-url { color: #333 !important; }
        }
      `}</style>

      <div className="flex items-start justify-between gap-3 flex-wrap no-print">
        <p className="text-xs text-slate-400 max-w-3xl">
          One page per station. Scan the QR with the phone or tablet that will run it, or
          send the link to whoever is staffing it — none of these need a login.{" "}
          <strong>Pin every door to its event</strong>: the last card takes no event and
          only reports what a badge is good for, so it never opens anything.
        </p>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 transition-colors"
        >
          <Printer className="h-3.5 w-3.5" />
          Print sheet
        </button>
      </div>

      {!keyRequired && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100/90 no-print">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" />
          <span>
            <strong>ACCESS_CONTROL_KEY is not set</strong>, so these pages are open to anyone
            who can reach the server — and scanning a guest ticket spends it. Set it in the
            backend <code>.env</code> and every link here will gain its <code>&amp;k=</code>
            {" "}automatically.
          </span>
        </div>
      )}

      <div className="qr-sheet grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {links.map((link) => (
          <div
            key={link.url}
            className="qr-card rounded-xl border border-slate-700/50 bg-slate-800/40 overflow-hidden flex flex-col"
          >
            <div className="h-1.5 w-full" style={{ backgroundColor: link.color }} />
            <div className="p-4 flex flex-col items-center gap-3 flex-1">
              <div className="text-center">
                <div className="text-sm font-semibold">{link.label}</div>
                {link.when && <div className="text-[11px] text-slate-400 mt-0.5">{link.when}</div>}
                {CAPTION[link.kind] && (
                  <div className="text-[11px] text-slate-400 mt-0.5">{CAPTION[link.kind]}</div>
                )}
              </div>

              <img
                src={link.qr}
                alt={`QR for ${link.label}`}
                className="w-40 h-40 bg-white rounded-lg p-1.5"
              />

              <div className="qr-url text-[10px] font-mono text-slate-400 break-all text-center leading-relaxed">
                {link.url}
              </div>

              <div className="flex items-center gap-1.5 mt-auto no-print">
                <button
                  onClick={() => copy(link.url)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 transition-colors"
                >
                  {copied === link.url ? (
                    <>
                      <Check className="h-3 w-3 text-emerald-300" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" /> Copy link
                    </>
                  )}
                </button>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> Open
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccessControl;
